import React, { useState, useEffect, useRef } from "react"
import Tabs from "aldoo-ra/Tabs"
import TabRail from "aldoo-ra/CMS/content-editor/tab-rail"
import useProductEditorTexts from "../useProductEditorTexts"
import Button from "aldoo-ra/Button"
import { getFieldEditor } from "aldoo-ra/CMS/field-editor-resolver"
import FieldLabel from "aldoo-ra/CMS/content-editor/FieldLabel"
import FieldSettingsButton from "aldoo-ra/CMS/field-settings-button"
import SubscriptionGroupEditor from "./subscription-group-editor"
import { isGroupVisible } from "aldoo-ra/CMS/content-editor/section-visibility"
import { builtinTabVisible } from "aldoo-ra/Admin/Store/extensions/builtin-tabs"
import { groupIconElement } from "aldoo-ra/CMS/content-editor/group-icon"
import { useUrlParam } from "aldoo-ra/URLState"
import ManageGroupsDialog from "aldoo-ra/Admin/Store/Subscriptions/ManageGroupsDialog"
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  KeyIcon,
  Squares2X2Icon,
  TagIcon,
} from "@heroicons/react/24/outline"

/**
 * Custom content editor for StoreProduct with redesigned layout:
 * - Tabbed interface with Details, Pricing, Availability, Licensing, Metadata
 * - Details tab: thumbnail, name, description, product ID
 * - Pricing tab: type, price configuration (1/2 width each)
 */
export default function StoreProductContentEditor({
  contentType,
  fieldValues,
  onChange,
  onSave,
  parentValues,
  readOnly = false,
  dataItem,
  enableFieldSettings = true,
  customActions,
  // the model's extensions: each visible section group is a tab of its own
  language,
  registerSection,
  visibilityContext,
  tabsOrientation = "horizontal",
  // the help center's key of a tab (TabRail helpKeyOf)
  helpKeyOf = null,
  close,
}) {
  // the open tab lives in the URL (`?ptab=`), so a refresh or a shared link
  // opens the product where it was left. `tab` is taken: the page that embeds
  // this browser (a vendor) keeps its own tabs there.
  const [tabParam, setActiveTab] = useUrlParam("ptab", { default: "" })
  // the tabs and the rail in the site's language (TextBundle store_product_editor)
  const t = useProductEditorTexts()
  const [showGroupsDialog, setShowGroupsDialog] = useState(false)
  const [groupsRefreshKey, setGroupsRefreshKey] = useState(0)
  const hasFocusedRef = useRef(false)
  const hasInitializedDefaults = useRef(false)

  // Check if this is a saved product (has _id and pid) - if so, disable auto-populate from the start
  const isExistingProduct = dataItem && dataItem._id && dataItem.pid
  const [pidManuallyEdited, setPidManuallyEdited] = useState(isExistingProduct)

  // Ensure fieldValues always has defaults applied (for validation)
  const effectiveFieldValues = React.useMemo(() => {
    const values = { ...fieldValues }
    contentType.fields.forEach((field) => {
      if (values[field.id] === undefined && field.default !== undefined) {
        values[field.id] = field.default
      }
    })
    return values
  }, [fieldValues, contentType.fields])

  // Check if bundle tab should be shown
  const showBundleTab = effectiveFieldValues?.type === "bundle" ||
                        (typeof effectiveFieldValues?.type === 'object' && effectiveFieldValues?.type?.value === "bundle")

  // Sync fieldValues with dataItem on mount (for editing existing products)
  useEffect(() => {
    if (!hasInitializedDefaults.current) {
      hasInitializedDefaults.current = true

      // If editing existing product, ensure fieldValues has all data from dataItem
      if (dataItem && dataItem._id) {
        const needsSync = Object.keys(dataItem).some(key => {
          return dataItem[key] !== undefined &&
                 fieldValues[key] === undefined &&
                 key !== '_id' && key !== '__v'
        })

        if (needsSync) {
          onChange?.({ ...dataItem, ...fieldValues })
          return
        }
      }

      // For new products, initialize with all defaults immediately
      if (!dataItem || !dataItem._id) {
        const needsDefaults = contentType.fields.some(field =>
          field.default !== undefined && fieldValues[field.id] === undefined
        )

        if (needsDefaults) {
          const valuesWithDefaults = { ...fieldValues }
          contentType.fields.forEach((field) => {
            if (valuesWithDefaults[field.id] === undefined && field.default !== undefined) {
              valuesWithDefaults[field.id] = field.default
            }
          })
          onChange?.(valuesWithDefaults)
        }
      }
    }
  }, []) // Empty deps to run only once on mount

  // Get current field values with defaults applied
  const getFieldValuesWithDefaults = () => {
    const valuesWithDefaults = { ...fieldValues }

    // Apply defaults for any undefined required fields
    contentType.fields.forEach((field) => {
      if (valuesWithDefaults[field.id] === undefined && field.default !== undefined) {
        valuesWithDefaults[field.id] = field.default
      }
    })

    return valuesWithDefaults
  }

  // Wrap onSave to ensure defaults are applied and formatting is run before saving
  const handleSave = async (data) => {
    // Merge data param with fieldValues to get most up-to-date values
    const currentValues = { ...fieldValues, ...data }
    const dataWithDefaults = { ...currentValues }

    // Apply defaults for any undefined required fields
    contentType.fields.forEach((field) => {
      if (dataWithDefaults[field.id] === undefined && field.default !== undefined) {
        dataWithDefaults[field.id] = field.default
      }
    })

    // Import formatting functions
    const { postFormatFields, getFormatter } = await import("aldoo-ra/CMS/field-formatter")

    // Post format the field values before saving
    const postFormattedValues = await postFormatFields(
      { ...dataWithDefaults },
      contentType
    )

    // Run Type.formatter if specified (for StoreProduct-specific formatting)
    if (contentType.formatter) {
      const formatter = getFormatter(contentType.formatter)
      if (formatter?.postFormat) {
        // Pass dataItem.localization to the formatter so it can sync localized fields
        const valuesWithMeta = {
          ...postFormattedValues,
          _localization: dataItem?.localization
        }
        await formatter.postFormat(valuesWithMeta, contentType)
        // Remove the metadata before saving
        delete valuesWithMeta._localization
        Object.assign(postFormattedValues, valuesWithMeta)
      }
    }

    const saveResult = await onSave?.(postFormattedValues)

    // Update fieldValues with the saved data (including _id for new products)
    if (saveResult && saveResult.result) {
      const savedId = saveResult.result
      // Check if result is just an _id string or a full object
      if (typeof savedId === 'string') {
        // Just an _id string - update fieldValues with the _id
        onChange?.({ ...fieldValues, _id: savedId })
      } else if (typeof savedId === 'object' && savedId._id) {
        // Full object with _id - merge it
        onChange?.({ ...fieldValues, ...savedId })
      }
    }

    return saveResult
  }

  // Focus the name field when component mounts if it has autoFocus
  useEffect(() => {
    if (!hasFocusedRef.current && !readOnly) {
      const nameField = getField("name")
      if (nameField?.render?.autoFocus) {
        // Wait for modal animation and rendering to complete
        const timeoutId = setTimeout(() => {
          // Find the input element by looking for name field
          const nameInput = document.querySelector('input[name="name"]')
          if (nameInput) {
            nameInput.focus()
            hasFocusedRef.current = true
          }
        }, 300)
        return () => clearTimeout(timeoutId)
      }
    }
  }, [readOnly])

  // Helper to get field config by id
  const getField = (fieldId) => {
    return contentType.fields.find((f) => f.id === fieldId)
  }

  // Utility to convert string to kebab-case
  const toKebabCase = (str) => {
    if (!str || typeof str !== 'string') return ''
    return str
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  }

  // Helper to handle field changes
  const handleFieldChange = (fieldId, value) => {
    const updatedValues = { ...fieldValues, [fieldId]: value }

    // Track if user manually edits pid (only if it differs from auto-generated value)
    if (fieldId === 'pid') {
      // Check if the new pid value differs from what would be auto-generated from name
      let nameValue = fieldValues.name
      if (typeof nameValue === 'object' && nameValue !== null) {
        nameValue = nameValue.en || nameValue.default || Object.values(nameValue)[0] || ''
      }
      const autoGeneratedPid = toKebabCase(nameValue)

      // Only mark as manually edited if the value differs from auto-generated
      if (value !== autoGeneratedPid) {
        setPidManuallyEdited(true)
      }
    }

    // Auto-populate pid from name if name changes and pid hasn't been manually edited
    if (fieldId === 'name' && !pidManuallyEdited) {
      // Extract string value in case it's wrapped in an object (localization)
      let nameValue = value
      if (typeof value === 'object' && value !== null) {
        // If it's a localized object, get the default language value
        nameValue = value.en || value.default || Object.values(value)[0] || ''
      }

      const kebabName = toKebabCase(nameValue)
      updatedValues.pid = kebabName
    }

    onChange?.(updatedValues)
  }

  // Helper to check if field is required
  const isFieldRequired = (field) => {
    if (typeof field.required === "function") {
      return field.required(fieldValues)
    }
    return field.required
  }

  // Helper to render a field editor
  const renderFieldEditor = (fieldId, options = {}) => {
    const field = getField(fieldId)
    if (!field) return null

    const FieldEditor = getFieldEditor(field, fieldValues)
    if (!FieldEditor) return null

    const isRequired = isFieldRequired(field)

    // Get field value, applying default if needed
    let fieldValue = fieldValues[fieldId]
    if (fieldValue === undefined && field.default !== undefined) {
      fieldValue = field.default
    }

    // Check if the field is read-only
    // This happens if the field has a localization setting turned on OR if field.readOnly is explicitly set to true
    let isReadOnly = readOnly || field.readOnly === true

    // Also check if field has localization enabled in the dataItem
    if (field.localization &&
        dataItem &&
        dataItem.localization &&
        Array.isArray(dataItem.localization) &&
        dataItem.localization.find((item) => item.field === field.id)) {
      isReadOnly = true
    }

    // Determine width class based on field.render.variant
    // Default to half-width (w-1/2) unless explicitly set to "full" or overridden by options
    // a phone has room for one field a row
    let widthClass = "w-full md:w-1/2"
    if (options.width) {
      widthClass = options.width
    } else if (field.render?.variant === "full") {
      widthClass = "w-full"
    }

    return (
      <div key={fieldId} className={`mb-4 ${widthClass} ${options.className || ""}`}>
        {/* the model's label and hint in the site's language */}
        <FieldLabel field={{ ...field, label: t(field.label), description: field.description && t(field.description) }} isRequired={isRequired} readOnly={isReadOnly} />
        <FieldEditor
          field={field}
          value={fieldValue}
          onChange={(val) => handleFieldChange(fieldId, val)}
          parentValues={fieldValues}
          parentObject={() => ({
            ...fieldValues,
            _contentType: contentType?.type
          })}
          readOnly={isReadOnly}
          customActions={customActions}
          autoFocus={field.render?.autoFocus}
          save={handleSave}
          // the dialog's language: an editor of a text that differs by language shows that one
          language={language}
        />
        {/* Localization support per field */}
        {enableFieldSettings && !!field.localization && (
          <FieldSettingsButton
            contentType={contentType}
            dataItem={dataItem}
            onContentSave={handleSave}
            field={field}
            fieldValues={fieldValues}
            setFieldValues={onChange}
            onSettingChange={(newDataItem) => {
              // Update dataItem reference
              dataItem = newDataItem
            }}
          />
        )}
      </div>
    )
  }

  // Define tabs — each with its place (`order`) among the extensions' (their `section.order`): what a product
  // needs to be sold comes first (its details, its price, its stock), what only some need after
  const builtinTabs = [
    { value: "details", label: "Details", icon: groupIconElement(DocumentTextIcon), order: 100 },
    { value: "pricing", label: "Pricing", icon: groupIconElement(CurrencyDollarIcon), order: 110 },
    { value: "availability", label: "Availability", icon: groupIconElement(ArchiveBoxIcon), order: 500 },
    { value: "licensing", label: "Licensing", icon: groupIconElement(KeyIcon), order: 530 },
    { value: "metadata", label: "Metadata", icon: groupIconElement(TagIcon), order: 1250 },
  ]

  // Add bundle tab if product type is bundle
  if (showBundleTab) {
    builtinTabs.push({ value: "bundle", label: "Bundle Configuration", icon: groupIconElement(Squares2X2Icon), order: 1260 })
  }

  // The tabs the enabled store product extensions bring (Admin → Store →
  // Settings → Extensions). A group with a component owns its tab;
  // one that brings plain fields gets them rendered like the built-in tabs.
  const sectionGroups = (contentType.groups || []).filter(
    (group) => group.extension && isGroupVisible(group, visibilityContext)
  )

  // a built-in tab an enabled extension took over is not shown twice — dropped
  // when it is replaced, rendered inside the extension's tab when absorbed
  const takenOver = (group) => [...(group.replaces || []), ...(group.absorbs || [])]
  const replaced = new Set(sectionGroups.flatMap(takenOver))
  // where a tab sits: an extension that asks to come first (`top`) opens the dialog; every other tab by its
  // `order` — a built-in's own, an extension's section order, else (none given) the place of the built-in it took
  // over (Information carries what Details did, so it opens the dialog like Details used to), else at 1000 on
  // (the extensions' default) in the order the model gave them; the sort is stable, so equals keep that order
  const slotOf = (group, index) => {
    if (group.top) return -1
    if (Number.isFinite(group.order)) return group.order
    const builtin = builtinTabs.find((tab) => takenOver(group).includes(tab.value))
    return builtin ? builtin.order : 1000 + index
  }

  const tabs = [
    ...builtinTabs
      // Details, Licensing and Metadata only where the product's content type lists them
      // (Pricing and Availability are every product's)
      .filter((tab) => !replaced.has(tab.value) && builtinTabVisible(tab.value, visibilityContext))
      .map((tab) => ({ tab, slot: tab.order })),
    ...sectionGroups.map((group, index) => {
      const icon = groupIconElement(group.icon)
      return {
        // an extension's mark beside its name (`section.badge`, rendered by the rail inside the editor's providers)
        tab: { value: group.id, label: group.label, ...(icon ? { icon } : {}), ...(group.badge ? { badge: group.badge } : {}) },
        slot: slotOf(group, index),
      }
    }),
  ]
    .sort((a, b) => a.slot - b.slot)
    .map((entry) => ({ ...entry.tab, label: t(entry.tab.label) }))

  // a tab the URL names but this product does not have — a tab an extension
  // replaced, or a link from another product — opens the first one instead,
  // without writing that back to the URL
  const activeTab = tabs.some((tab) => tab.value === tabParam) ? tabParam : tabs[0]?.value

  const activeSection = sectionGroups.find((group) => group.id === activeTab)

  /** What a built-in tab renders, for an extension that absorbed it. */
  const renderBuiltinTab = (id) => {
    if (id === "details") return renderDetailsTab()
    if (id === "pricing") return renderPricingTab()
    if (id === "licensing") return renderLicensingTab()
    return (
      <div>
        {getFieldsForTab(id).map((field) => (
          <div key={field.id} className="mb-4">
            {renderFieldEditor(field.id)}
          </div>
        ))}
      </div>
    )
  }

  // the fields other extensions put into a tab (`extends`, aldoo-ra/Admin/Store/extensions),
  // under what the tab itself shows — in the first of the tabs they extend
  // that this product shows
  const extendedFieldsOf = (tabId) => {
    const shown = tabs.map((tab) => tab.value)
    return (contentType.fields || []).filter(
      (field) =>
        field.extendsTab &&
        (field.extendsTabs || [field.extendsTab]).find((id) => shown.includes(id)) === tabId &&
        isGroupVisible({ visible: field.extensionVisible }, visibilityContext)
    )
  }
  const renderExtendedFields = (tabId) => {
    const fields = extendedFieldsOf(tabId)
    if (!fields.length) return null
    return (
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`extended-fields-${tabId}`}>
        {fields.map((field) => (
          <div key={field.id}>{renderFieldEditor(field.id, { width: "w-full" })}</div>
        ))}
      </div>
    )
  }

  const renderSection = (group) => {
    if (!group.component) {
      const fields = (contentType.fields || []).filter((field) => field.group === group.id && !field.extendsTab)
      return (
        <div>
          {fields.map((field) => (
            <div key={field.id} className="mb-4">
              {renderFieldEditor(field.id)}
            </div>
          ))}
        </div>
      )
    }
    const Section = group.component
    return (
      <Section
        group={group}
        contentType={contentType}
        product={dataItem}
        dataItem={dataItem}
        value={fieldValues}
        language={language}
        onChange={(values) => onChange({ ...fieldValues, ...values })}
        renderField={renderFieldEditor}
        // the fields other extensions put into this tab, for a section that lays them out itself
        extendedFields={group.placesExtendedFields ? extendedFieldsOf(group.id) : []}
        register={(handlers) => registerSection?.(group.id, handlers)}
        close={close}
      />
    )
  }

  // Render content for Details tab
  const renderDetailsTab = () => (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="w-full md:w-80 flex-shrink-0">
        <div className="thumbnail-editor-centered [&_.image-editor]:flex [&_.image-editor]:flex-col [&_.image-editor]:items-center">
          {renderFieldEditor("thumbnail", { width: "w-full" })}
        </div>
      </div>
      <div className="flex-1 space-y-4">
        {renderFieldEditor("name", { width: "w-full" })}
        {renderFieldEditor("pid", { width: "w-full" })}
        {renderFieldEditor("description", { width: "w-full" })}
      </div>
    </div>
  )

  const isSubscription = effectiveFieldValues?.type === "subscription" ||
    (typeof effectiveFieldValues?.type === "object" && effectiveFieldValues?.type?.value === "subscription")

  // Render content for Pricing tab
  const renderPricingTab = () => (
    <div className="space-y-4">
      {/* what the product is sold as belongs with its price, not with what it is */}
      {renderFieldEditor("type", { width: "w-full" })}
      {renderFieldEditor("price", { width: "w-full" })}
      {isSubscription && (
        <div className="mb-4 w-full">
          <div className="flex items-center gap-3 mb-2">
            <label className="font-semibold text-admin_text">{t`Subscription Group`}</label>
            <button
              type="button"
              onClick={() => setShowGroupsDialog(true)}
              className="text-xs text-admin_primary hover:underline"
            >
              {t`See subscription groups`}
            </button>
          </div>
          <SubscriptionGroupEditor
            value={effectiveFieldValues.subscriptionGroup}
            onChange={(val) => handleFieldChange("subscriptionGroup", val)}
            readOnly={readOnly}
            refreshKey={groupsRefreshKey}
          />
        </div>
      )}
      <ManageGroupsDialog
        isOpen={showGroupsDialog}
        onClose={() => {
          setShowGroupsDialog(false)
          // Refresh the group dropdown to pick up any added/removed groups.
          setGroupsRefreshKey((k) => k + 1)
        }}
      />
    </div>
  )

  // Render content for Licensing tab
  const renderLicensingTab = () => (
    <div className="space-y-4">
      {renderFieldEditor("licenseModel", { width: "w-full" })}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderFieldEditor("supported_platforms", { width: "w-full" })}
        {renderFieldEditor("seats", { width: "w-full" })}
      </div>
    </div>
  )

  // Get fields for each tab based on groups
  const getFieldsForTab = (tabValue) => {
    return contentType.fields.filter((field) => {
      // an extension's fields in a built-in tab render after it (renderExtendedFields)
      if (field.extendsTab) return false
      if (tabValue === "availability") return field.group === "availability"
      if (tabValue === "licensing") return field.group === "licensing"
      if (tabValue === "metadata") return field.group === "meta"
      if (tabValue === "bundle") return field.group === "bundle"
      return false
    })
  }

  const scrolls = tabsOrientation === "vertical"
  const tabContent = (
    <div className={`min-h-[200px] flex-1 min-w-0 w-full max-w-full overflow-x-hidden [&_img]:max-w-full [&_table]:w-full [&_table]:max-w-full [&_input]:max-w-full [&_textarea]:max-w-full ${scrolls ? "overflow-y-auto h-full md:pr-2 pb-24 scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper" : ""}`}>
      {activeSection && (activeSection.absorbs || []).map((id) => (
        <div key={id} className="mb-6">
          {renderBuiltinTab(id)}
        </div>
      ))}
      {activeSection && renderSection(activeSection)}
      {activeSection && !activeSection.placesExtendedFields && renderExtendedFields(activeSection.id)}
      {!activeSection && activeTab === "details" && renderDetailsTab()}
      {!activeSection && activeTab === "pricing" && renderPricingTab()}
      {!activeSection && activeTab === "licensing" && renderLicensingTab()}
      {!activeSection && (activeTab === "availability" || activeTab === "metadata" || activeTab === "bundle") && (
        <div>
          {getFieldsForTab(activeTab).map((field) => (
            <div key={field.id} className="mb-4">
              {renderFieldEditor(field.id)}
            </div>
          ))}
        </div>
      )}
      {!activeSection && renderExtendedFields(activeTab)}
    </div>
  )

  // a model composed of extensions has more tabs than a strip can carry
  if (tabsOrientation === "vertical") {
    return (
      // on a phone the rail is a bar above the fields (TabRail), so the two stack
      <div className="store-product-content-editor flex flex-col md:flex-row md:gap-4 items-stretch h-full min-h-0">
        <TabRail tabs={tabs} value={activeTab} onChange={setActiveTab} texts={{ find: t`Find a tab...`, sections: t`Sections` }} helpKeyOf={helpKeyOf} />
        {tabContent}
      </div>
    )
  }

  return (
    <div className="store-product-content-editor">
      {/* Tabs */}
      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={setActiveTab}
        variant="admin"
        className="mb-6"
      />

      {/* Tab Content */}
      {tabContent}
    </div>
  )
}
